import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { differenceInDays } from 'date-fns';

// Models
import { HotelFareModel } from '@src/shared/src/models';

// Utils
import { formatCents } from '@src/shared/src/util/general';

// Constants
import {
  ACCOMMODATION_TYPES,
  CENTS_CONVERSION_TYPES,
  ENVIRONMENT,
  AGGREGATORS,
} from '@src/shared/src/const/app';

// Store
import { IRootState } from '@src/store';

// Constants
import { FARE_PAYMENT_TYPE } from '@src/util/const';

// Components
import { HotelFareRefundableUntil } from './HotelFareRefundableUntil';
import { HotelFareSelectionButton } from './HotelFareSelectionButton';
import { Co2EmissionLabel } from '@src/components/Co2EmissionLabel';

type Props = {
  fare: HotelFareModel;
  accommodationType: ACCOMMODATION_TYPES;
  env: ENVIRONMENT;
  isBasketEditable?: boolean;
  onSelectHotel?: (hotelFareIds: string[]) => void;
  isSelectingHotel?: boolean;
};

export const HotelFare: React.FC<Props> = ({
  fare,
  accommodationType,
  env,
  isBasketEditable,
  onSelectHotel,
  isSelectingHotel,
}) => {
  const { t } = useTranslation();
  const hasBreakfast = fare.breakfast;

  const search = useSelector((state: IRootState) => state.search.currentSearch);

  const nights = differenceInDays(search.arrAt, search.depAt);

  const calculatePricePerNight = (totalPrice: number) =>
    formatCents(totalPrice / nights, CENTS_CONVERSION_TYPES.FLOOR);

  const getBreakfastInfo = (fare: HotelFareModel) => {
    if (fare.aggregator?.ident === AGGREGATORS.EXPEDIA_HOTEL && !fare.breakfast) {
      return t('hotelFare.tooltip.expedia.breakfastInfo');
    }
    if (fare.breakfastSurcharge && !fare.breakfast) {
      return t('hotelFare.tooltip.breakfastSurcharge', {
        breakfastSurcharge: formatCents(fare.breakfastSurcharge),
      });
    }
  };

  return (
    <div className="hotel-fare-group-item">
      <div className="hotel-fare-group-item-features">
        <div className="hotel-fare-group-item-feature">
          <span data-rh={getBreakfastInfo(fare)}>
            <i className={`material-symbols ${hasBreakfast ? 'is--green' : 'is--red'}`}>
              {hasBreakfast ? 'check_circle' : 'cancel'}
            </i>
            {hasBreakfast
              ? t('hotelFare.breakfast.included')
              : t('hotelFare.breakfast.notIncluded')}
          </span>
        </div>
        <div className="hotel-fare-group-item-feature">
          <HotelFareRefundableUntil
            refundableUntil={fare.refundableUntil}
            conditions={fare.conditions}
            accommodationType={accommodationType}
            deepLink={fare.deepLink}
          />
        </div>
        <div className="hotel-fare-group-item-tags">
          {fare.paymentType === FARE_PAYMENT_TYPE.PREPAY && (
            <span className="hotel-fare-group-item-tag" data-rh={t('hotelFare.cell.price.tooltip.prepay')}>
              <i className="material-symbols">toll</i>
              {t('hotelFare.select.paymentType.prepaid')}
            </span>
          )}
          {fare.negotiatedRate && (
            <span
              className="hotel-fare-group-item-tag hotel-fare-group-item-tag-company"
              data-rh={t('hotel.tooltip.fare.negotiatedRates')}>
              <i className="material-symbols">attach_money</i>
              {t('hotel.fare.negotiatedRates')}
            </span>
          )}
        </div>
      </div>
      <div className="hotel-fare-group-item-price">
        <div className="hotel-fare-group-item-price-total">{formatCents(fare.price)}</div>
        <span data-rh={t('trip.tooltip.body.text.co2ValueDescription')} data-rh-at="bottom">
          <Co2EmissionLabel value={fare.co2InKg} />
        </span>
        <div className="hotel-fare-group-item-price-details">
          {calculatePricePerNight(fare.price)}&nbsp;{t('hotelFare.label.pricePerNight')}
          {fare.provider && (
            <span className="hotel-fare-group-item-price-provider">
              {t('hotelFare.cell.price.provider.via')} {fare.provider}
            </span>
          )}
        </div>
      </div>
      <HotelFareSelectionButton
        fareId={fare.id}
        farePaymentType={fare.paymentType}
        env={env}
        isBasketEditable={isBasketEditable}
        onSelectHotel={onSelectHotel}
        isSelectingHotel={isSelectingHotel}
      />
    </div>
  );
};
